import React, { useEffect } from 'react';
import { ApolloError } from '@apollo/client';

import { Headline } from '@xingternal/typography';

import { useCompanyData } from '@/contexts/CompaniesDataContext';
import { FollowButton } from '@/components/FollowButton';
import { CompanyCard } from '@/components/CompanyCard';
import { CompanyCardContainer } from '@/components/CompanyCardContainer';
import { Pagination } from '@/components/Pagination';
import { MetaData } from '@/components/MetaData';
import { PageError } from '@/components/PageError';
import { CompanyCardsSkeletonLoggedoutPage } from '@/components/Skeleton';
import { MAX_LOGGED_OUT_ITEMS } from '@/config';

import { Company } from '@/types/graphql-types';

const CompanyLoggedout = ({
  companies,
  total,
  headerText,
  secondaryHeader,
  authenticated,
  limit,
  activePage,
  changePage,
  loading,
  error,
  styles,
}: {
  companies: Company[] | undefined;
  total: number | undefined;
  headerText: string;
  authenticated: boolean;
  secondaryHeader?: JSX.Element;
  limit: number;
  activePage: number;
  changePage: any;
  loading: boolean;
  error: ApolloError | undefined;
  styles: any;
}) => {
  const { addCompanies, globalCompaniesState } = useCompanyData();
  useEffect(() => {
    if (companies) {
      addCompanies({
        companies,
      });
    }
  }, [companies]);

  if (loading) return <CompanyCardsSkeletonLoggedoutPage />;
  if (error) return <PageError />;
  return (
    <>
      <MetaData index title={headerText} description={headerText} />
      <Headline size="medium" as="h1" className={styles.headline}>
        {headerText}
      </Headline>
      {secondaryHeader}
      <CompanyCardContainer>
        {companies?.map((company) => {
          const isFollowing =
            company?.userContext?.followState?.isFollowing || false;

          return (
            <CompanyCard
              key={company?.id}
              company={company}
              buttons={
                authenticated && globalCompaniesState.length > 0
                  ? [
                      <FollowButton
                        className={styles.followButton}
                        disabled={company?.entityPageId ? false : true}
                        entityPageId={company?.entityPageId || ''}
                        key={company?.entityPageId}
                        isFollowing={isFollowing}
                        companyId={company?.id}
                      />,
                    ]
                  : []
              }
            />
          );
        })}
      </CompanyCardContainer>
      {companies && (
        <Pagination
          currentPage={activePage}
          perPage={limit}
          total={Math.min(MAX_LOGGED_OUT_ITEMS, total as number)}
          onPageClick={changePage}
        />
      )}
    </>
  );
};

export { CompanyLoggedout };
